import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  IconButton,
  Modal,
  TableRow,
  TextField,
  Button,
  Tooltip,
  useTheme,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  
  Close as CloseIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const QuizResult = () => {
  const theme = useTheme();
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const[showUserGuideModal,setShowUserGuideModal]=useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // Static data for dropdowns
  const staticCourseData = [
    { id: 1, name: 'Mathematics' },
    { id: 2, name: 'Science' },
    { id: 3, name: 'History' },
  ];

  const staticModuleData = [
    { id: 1, name: 'Introduction' },
    { id: 2, name: 'Advanced' },
    { id: 3, name: 'Practical' },
  ];

  const staticTopicData = [
    { id: 1, name: 'Basics' },
    { id: 2, name: 'Intermediate' },
    { id: 3, name: 'Advanced Topics' },
  ];

  // Static quiz result data
  const staticQuizResultData = [
    { id: 1, studentName: 'Student 1', marksObtained: 85, status: 'Pass' },
    { id: 2, studentName: 'Student 2', marksObtained: 45, status: 'Fail' },
    { id: 3, studentName: 'Student 3', marksObtained: 70, status: 'Pass' },
  ];

  const [quizResultData, setQuizResultData] = useState(staticQuizResultData);

  const handleCourseChange = (event) => {
    setSelectedCourse(event.target.value);
  };

  const handleModuleChange = (event) => {
    setSelectedModule(event.target.value);
  };

  const handleTopicChange = (event) => {
    setSelectedTopic(event.target.value);
  };

  const handleSave = () => {
    if (!selectedCourse || !selectedModule || !selectedTopic) {
      setSnackbarMessage('Please select all required fields: Course, Module, and Topic.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }
    // Placeholder for save logic
    setSnackbarMessage('Quiz results saved successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
  };

  useEffect(() => {
    // Reset quiz result data when course, module, or topic changes
    setQuizResultData(staticQuizResultData);
  }, [selectedCourse, selectedModule, selectedTopic]);

  const isFormFilled = selectedCourse && selectedModule && selectedTopic;

  return (
    <Container sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>

      <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent:'flex-end',
            mb:1,
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 4,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
        }}
      >
        
        <FormControl sx={{ width: 300, maxWidth: '100%' }}>
          <InputLabel
            id="course-label"
            sx={{ color: theme.palette.text.primary }}
          >
            Course
          </InputLabel>
          <Select
            labelId="course-label"
            value={selectedCourse}
            label="Course"
            onChange={handleCourseChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select a course</em>
            </MenuItem>
            {staticCourseData.map((course) => (
              <MenuItem key={course.id} value={course.name}>
                {course.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 300, maxWidth: '100%', ml: 2 }}>
          <InputLabel
            id="module-label"
            sx={{ color: theme.palette.text.primary }}
          >
            Module
          </InputLabel>
          <Select
            labelId="module-label"
            value={selectedModule}
            label="Module"
            onChange={handleModuleChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select a module</em>
            </MenuItem>
            {staticModuleData.map((module) => (
              <MenuItem key={module.id} value={module.name}>
                {module.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 300, maxWidth: '100%', ml: 2 }}>
          <InputLabel
            id="topic-label"
            sx={{ color: theme.palette.text.primary }}
          >
            Topic
          </InputLabel>
          <Select
            labelId="topic-label"
            value={selectedTopic}
            label="Topic"
            onChange={handleTopicChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select a topic</em>
            </MenuItem>
            {staticTopicData.map((topic) => (
              <MenuItem key={topic.id} value={topic.name}>
                {topic.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          sx={{
            width: 300,
            maxWidth: '100%',
            ml: 2,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
          }}
          label="Date"
          type="date"
          defaultValue={new Date().toISOString().split('T')[0]}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            readOnly: true,
          }}
        />
      </Box>
      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        {isFormFilled && (
          <Box sx={{ width: '100%', overflowX: 'auto',mt:4}}>
            <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
              <Table
                sx={{
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: '1.1rem',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Marks Obtained</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quizResultData.length > 0 ? (
                    quizResultData.map((result, index) => (
                      <TableRow
                        key={result.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <TableCell sx={{ fontSize: '1rem' }}>{index + 1}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{result.studentName}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{result.marksObtained}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>
                          <Typography
                            color={result.status === 'Pass' ? theme.palette.success.main : theme.palette.error.main}
                          >
                            {result.status}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No quiz results found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Tooltip
          title={
            !selectedCourse
              ? 'Select a Course'
              : !selectedModule
              ? 'Select a Module'
              : !selectedTopic
              ? 'Select a Topic'
              : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isFormFilled}
              sx={{
                width: '160px',
                borderRadius: '10px',
                height: '50px',
                padding: '10px 0',
                fontWeight: 'bold',
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Quiz Result Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Content */}
         <Box sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Select Filters */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to filter quiz results:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the <strong>Course</strong> dropdown to select a course (for example, Mathematics or Science).
    </li>
    <li>
      Select a <strong>Module</strong> from the next dropdown (for example, Introduction or Advanced).
    </li>
    <li>
      Choose a <strong>Topic</strong> to further refine the results (for example, Basics or Intermediate).
    </li>
    <li>
      The current date is automatically populated and displayed as read-only for reference.
    </li>
    <li>
      Once all filters are selected, the quiz results table will be displayed below with student details.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: All three filters (Course, Module, and Topic) must be selected to view quiz results.
  </Typography>

  {/* Section 2: View Quiz Results */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Viewing the quiz results table:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      After applying the filters, the table displays <strong>S.No</strong>, <strong>Student Name</strong>, <strong>Marks Obtained</strong>, and <strong>Status</strong>.
    </li>
    <li>
      The status is color-coded: <strong>Pass</strong> appears in green, and <strong>Fail</strong> appears in red.
    </li>
    <li>
      Hover over a row to see a subtle highlight effect for better readability.
    </li>
    <li>
      If no matching data is found, the message <strong>"No quiz results found"</strong> will be displayed.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: In this demo, data resets to static placeholder values when filters change.
  </Typography>

  {/* Section 3: Save Quiz Results */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to save quiz results:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Ensure all required filters (Course, Module, and Topic) are selected.
    </li>
    <li>
      Review the displayed table data, if necessary.
    </li>
    <li>
      Click the <strong>"Save"</strong> button located at the bottom-right corner.
    </li>
    <li>
      A success snackbar will appear to confirm the save action (placeholder logic in this component).
    </li>
    <li>
      If any filter is missing, the Save button will be disabled and a tooltip will indicate the missing requirement.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: In a production environment, this action would persist data to the backend.
  </Typography>

  {/* Section 4: Error Handling */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Handling errors and notifications:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      If an attempt is made to save without completing all filters, an error snackbar will appear at the top.
    </li>
    <li>
      Snackbars automatically disappear after 4 seconds or can be closed manually.
    </li>
    <li>
      Success notifications are shown in green, while error messages appear in red.
    </li>
  </ul>

  {/* Section 5: General Navigation */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Additional tips:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the info icon again at any time to reopen this guide.
    </li>
    <li>
      The layout is fully responsive; on smaller screens, elements may stack or scroll horizontally.
    </li>
    <li>
      Theme-aware styling ensures a consistent experience across light and dark modes.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: This is a static demonstration. Integrate with APIs to enable dynamic data handling.
  </Typography>
</Box>

        </Box>
      </Modal>
    </Container>
  );
};

export default QuizResult;